import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
// import api from "../../lib/api.ts"; // Not directly needed if using vehicleServices.ts
import CurrencyDisplay from "../common/CurrencyDisplay.jsx";
import { requestVehicleReturn } from "../../services/vehicleServices.ts";
import { useToast } from "../../context/ToastContext";
// --- START: Modal Component (No changes needed here) ---
const ConfirmationModal = ({ isOpen, onClose, onConfirm, vehicleName }) => {
  if (!isOpen) return null;

  return (
    // Fixed overlay/backdrop for the modal
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      aria-modal="true"
      role="dialog"
    >
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Request Vehicle Return
        </h3>
        <p className="text-gray-700 mb-6">
          Are you sure you want to request the return of
          <span className="font-bold text-red-600"> "{vehicleName}"</span>? This
          action cannot be undone.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
          >
            Request
          </button>
        </div>
      </div>
    </div>
  );
};
// --- END: Modal Component ---

const VehicleListCard = ({ vehicle }) => {
  const {
    id, // <--- This is the vehicleId you need to use
    title,
    slug,
    lotNo,
    vin,
    miles,
    cylinder,
    fuelType,
    engine,
    startBidding,
    currentBidding,
    image,
    sellerName = "",
    highlights = [],
    ProductStatus,
    rejectionReason = "",
    auctionName = "",
    reservedPrice = 0,
  } = vehicle;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  /**
   * CORRECTED: Made the function async to use await, and added error handling.
   * Passes the destructured 'id' as the vehicleId.
   */
  const handleConfirmReturn = async () => {
    // Close the modal immediately for better user experience
    setIsModalOpen(false);

    try {
      // 1. Pass the correct ID variable (`id`) to the service function
      const response = await requestVehicleReturn({ vehicleId: id });

      // OPTIONAL: Add success feedback here (e.g., a toast notification)
      console.log("Return request submitted successfully:", response);
      showSuccess("Vehicle return request submitted successfully");
      window.location.reload();
      // OPTIONAL: You might want to refresh the list or update the status locally
      // e.g., onSuccessfulReturn();
    } catch (error) {
      // 2. Handle API error (e.g., show an error toast/message)
      console.error("Error requesting vehicle return:", error);
      showError(
        error?.response?.data?.message || "Failed to request vehicle return",
      );
      // OPTIONAL: Show a user-friendly error message
      // e.g., alert('Failed to request return. Please try again.');
    }
  };

  return (
    <>
      <div className="grid grid-cols-12 gap-4 items-center px-2 py-1 hover:bg-gray-50 transition-colors duration-200">
        {/* ... (Existing Vehicle Card Content) ... */}

        {/* Vehicle Image */}
        <div className="col-span-1">
          <img
            src={image || "/car-placeholder.png"}
            alt={title}
            className="w-16 h-12 object-cover rounded"
          />
        </div>
        {/* Vehicle Info */}
        <div className="col-span-3">
          <h3 className="font-semibold text-gray-800 text-sm truncate mb-1">
            {title}
          </h3>
          <div className="text-xs text-gray-500 mb-1">
            Lot: <span className="font-medium">{lotNo}</span>
          </div>
          <div className="text-xs text-gray-500 truncate mb-2">
            VIN:{" "}
            {vin
              ? `${"*".repeat(Math.max(0, vin.length - 5))}${vin.slice(-5)}`
              : "N/A"}
          </div>

          {/* Highlights */}
          {highlights.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {highlights.slice(0, 2).map((highlight, i) => {
                const isObject =
                  typeof highlight === "object" && highlight !== null;
                const name = isObject ? highlight.name : highlight;
                const bgColor = isObject ? highlight.bgColor : "#f97316";
                const textColor = isObject ? highlight.textColor : "#fff";

                return (
                  <span
                    key={i}
                    className="px-2 py-[2px] rounded text-[10px] font-medium shadow-sm"
                    style={{
                      backgroundColor: bgColor,
                      color: textColor,
                    }}
                  >
                    {name}
                  </span>
                );
              })}
              {highlights.length > 2 && (
                <span className="bg-gray-600 text-white px-2 py-[2px] rounded text-[10px] font-medium shadow-sm">
                  +{highlights.length - 2}
                </span>
              )}
            </div>
          )}
        </div>
        {/* Specs or Rejection Reason */}
        {(ProductStatus === "Under Approval" || ProductStatus === "Sold") && (
          <div className="col-span-1 text-xs text-gray-600 text-left">
            <span className="font-medium">
              <CurrencyDisplay
                amount={vehicle.soldPrice}
                size="small"
                signOptions={{ width: "12px" }}
              />
            </span>
          </div>
        )}
        {ProductStatus !== "Under Approval" && ProductStatus !== "Sold" && (
          <div className="col-span-1 text-xs text-gray-600 text-left">
            <span className="font-medium">
              <CurrencyDisplay
                amount={vehicle.reservedPrice || 0}
                size="small"
                signOptions={{ width: "12px" }}
              />
            </span>
          </div>
        )}
        {ProductStatus === "Rejected" ? (
          <div className="col-span-7 text-[10px] text-red-600 text-left">
            <span className="font-medium">{rejectionReason || "-"}</span>
          </div>
        ) : (
          <>
            <div className="col-span-1 text-xs text-gray-600 text-left">
              <span className="font-medium">
                {miles?.toLocaleString() || "-"}
              </span>
            </div>
            <div className="col-span-1 text-xs text-gray-600 text-left">
              <span className="font-medium">{fuelType || "-"}</span>
            </div>
            <div className="col-span-1 text-xs text-gray-600 text-left">
              <span className="font-medium">{cylinder || "-"}</span>
            </div>
            <div className="col-span-1 text-xs text-gray-600 text-left">
              <span className="font-medium">{engine || "-"}</span>
            </div>
            <div className="col-span-1 text-xs text-gray-600 text-left">
              <span className="font-medium">
                {vehicle.updatedAt
                  ? new Date(vehicle.updatedAt).toLocaleDateString()
                  : "-"}
              </span>
            </div>
            {vehicle.showAuctionColumn && (
              <div className="col-span-1 text-xs text-gray-600 text-left">
                <span className="font-medium">{auctionName || "-"}</span>
              </div>
            )}
            <div className="col-span-1"></div>
          </>
        )}
        {/* Actions */}
        <div className="col-span-1 flex items-end justify-end gap-2">
          <button
            onClick={() => navigate(`/${slug}`)}
            className="px-1 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100 transition-colors duration-200"
          >
            Details
          </button>
          {/* Request Return Button */}
          {ProductStatus === "Stock" && (
            <div>
              <button
                onClick={() => setIsModalOpen(true)} // Opens the modal
                className="px-1 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors duration-200"
              >
                Request Return
              </button>
            </div>
          )}
          {/* Edit button */}
          {(ProductStatus === "Rejected" ||
            ProductStatus?.name === "Rejected" ||
            ProductStatus === "Waiting" ||
            ProductStatus?.name === "Waiting" ||
            ProductStatus === "Awaiting" ||
            ProductStatus?.name === "Awaiting") &&
            !(
              ProductStatus === "Awaiting Arrival" ||
              ProductStatus?.name === "Awaiting Arrival"
            ) && (
              <button
                onClick={() => navigate(`/addvehicles?edit=${id}`)}
                className="px-1 py-1 text-xs bg-gradient-to-r from-[#FF4A33] to-[#ffa033] text-white rounded hover:shadow-lg transition-all duration-200"
              >
                Edit
              </button>
            )}
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmReturn} // Calls the async function
        vehicleName={title}
      />
    </>
  );
};

export default VehicleListCard;
